import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import styles from "./RecordPayment.module.css";

interface AdjustmentFormData {
  billingNum: string;
  amount: string;
  invoiceId: string;
  receivedMonth: string;
  receivedDay: string;
  receivedYear: string;
  receivedHour: string;
  receivedMinute: string;
  receivedAmPm: string;
  notes: string;
}

interface InvoiceOption {
  id: string;
  number: string;
  description: string;
}

interface PurchaseLineItem {
  id: number;
  purchaseText: string;
  onetime: number;
  prorated: number;
  monthly: number;
  qty: number;
  skuId: number;
}

/**
 * Allows sysadmin to record an adjustment on a billing account number,
 * optionally linked to a specific invoice.
 *
 * Route: /sysadmin/adjustment/:accountNumId
 */
const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const RecordAdjustment: React.FC = () => {
  const { accountNumId } = useParams<{ accountNumId: string }>();
  const now = new Date();
  const [formData, setFormData] = useState<AdjustmentFormData>({
    billingNum: "",
    amount: "",
    invoiceId: "",
    receivedMonth: months[now.getMonth()],
    receivedDay: now.getDate().toString().padStart(2, "0"),
    receivedYear: now.getFullYear().toString(),
    receivedHour: (((now.getHours() + 11) % 12) + 1).toString().padStart(2, "0"),
    receivedMinute: now.getMinutes().toString().padStart(2, "0"),
    receivedAmPm: now.getHours() >= 12 ? "pm" : "am",
    notes: "",
  });
  const [invoiceOptions, setInvoiceOptions] = useState<InvoiceOption[]>([]);
  const [purchases, setPurchases] = useState<PurchaseLineItem[]>([]);
  const [selectedPurchaseIds, setSelectedPurchaseIds] = useState<Set<number>>(new Set());
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const latestInvoiceIdRef = useRef<string>("");

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount/accountNumId change only
  useEffect(() => {
    if (accountNumId) {
      fetchAccountInfo();
      fetchInvoiceOptions();
    }
  }, [accountNumId]);

  const fetchAccountInfo = async () => {
    try {
      const response = await callApi(
        `sysadmin/adjustment/accountinfo/${accountNumId}`,
        HttpMethod.GET,
      );
      setFormData((prev) => ({
        ...prev,
        billingNum: response.billingNum || "",
      }));
    } catch (err: any) {
      setError("Failed to load account information.");
    }
  };

  const fetchInvoiceOptions = async () => {
    try {
      const response = await callApi(
        `sysadmin/adjustment/invoices/${accountNumId}`,
        HttpMethod.GET,
      );
      setInvoiceOptions(response || []);
    } catch (err: any) {
      setError("Failed to load invoice options.");
    }
  };

  const fetchPurchasesForInvoice = async (invoiceId: string) => {
    latestInvoiceIdRef.current = invoiceId;

    if (!invoiceId) {
      setPurchases([]);
      setSelectedPurchaseIds(new Set());
      return;
    }

    setPurchasesLoading(true);
    setSelectedPurchaseIds(new Set());
    try {
      const response = await callApi(
        `sysadmin/adjustment/purchases/${invoiceId}`,
        HttpMethod.GET,
      );
      // Only apply if this is still the selected invoice
      if (latestInvoiceIdRef.current !== invoiceId) return;
      const items: PurchaseLineItem[] = response || [];
      setPurchases(items);
      setSelectedPurchaseIds(new Set(items.map((p) => p.id)));
    } catch (err: any) {
      if (latestInvoiceIdRef.current !== invoiceId) return;
      setError("Failed to load purchases for this invoice.");
      setPurchases([]);
    } finally {
      if (latestInvoiceIdRef.current === invoiceId) {
        setPurchasesLoading(false);
      }
    }
  };

  const handleCancel = () => {
    const now = new Date();
    setFormData((prev) => ({
      ...prev,
      amount: "",
      invoiceId: "",
      receivedMonth: months[now.getMonth()],
      receivedDay: now.getDate().toString().padStart(2, "0"),
      receivedYear: now.getFullYear().toString(),
      receivedHour: (((now.getHours() + 11) % 12) + 1).toString().padStart(2, "0"),
      receivedMinute: now.getMinutes().toString().padStart(2, "0"),
      receivedAmPm: now.getHours() >= 12 ? "pm" : "am",
      notes: "",
    }));
    setPurchases([]);
    setSelectedPurchaseIds(new Set());
    setError("");
    setSuccessMessage("");
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Clamp day when month or year changes
      if (name === "receivedMonth" || name === "receivedYear") {
        const maxDay = getDaysInMonth(updated.receivedMonth, updated.receivedYear);
        if (parseInt(updated.receivedDay) > maxDay) {
          updated.receivedDay = maxDay.toString().padStart(2, "0");
        }
      }

      return updated;
    });

    // Fetch purchases when invoice selection changes
    if (name === "invoiceId") {
      fetchPurchasesForInvoice(value);
    }
  };

  const handlePurchaseToggle = (purchaseId: number) => {
    setSelectedPurchaseIds((prev) => {
      const next = new Set(prev);
      if (next.has(purchaseId)) {
        next.delete(purchaseId);
      } else {
        next.add(purchaseId);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      // Build ISO 8601 date from form components
      const monthIndex = months.indexOf(formData.receivedMonth) + 1;
      if (monthIndex < 1 || monthIndex > 12) {
        setError("Invalid month selected.");
        setIsLoading(false);
        return;
      }
      let hour24 = parseInt(formData.receivedHour);
      if (formData.receivedAmPm === "pm" && hour24 !== 12) hour24 += 12;
      if (formData.receivedAmPm === "am" && hour24 === 12) hour24 = 0;
      const isoDate = `${formData.receivedYear}-${monthIndex.toString().padStart(2, "0")}-${formData.receivedDay}T${hour24.toString().padStart(2, "0")}:${formData.receivedMinute}:00`;

      const parsedAccountNumId = parseInt(accountNumId || "");
      if (!Number.isFinite(parsedAccountNumId) || parsedAccountNumId <= 0) {
        setError("Invalid account number.");
        setIsLoading(false);
        return;
      }

      const parsedAmount = parseFloat(formData.amount);
      if (!Number.isFinite(parsedAmount) || parsedAmount === 0) {
        setError("Amount must be a valid non-zero number.");
        setIsLoading(false);
        return;
      }

      if (!formData.invoiceId) {
        setError("An invoice must be selected.");
        setIsLoading(false);
        return;
      }

      if (selectedPurchaseIds.size === 0) {
        setError("At least one purchase must be selected.");
        setIsLoading(false);
        return;
      }

      const adjustmentData = {
        accountNumId: parsedAccountNumId,
        amount: parsedAmount,
        invoiceId: parseInt(formData.invoiceId),
        receivedDate: isoDate,
        notes: formData.notes,
        purchaseIds: Array.from(selectedPurchaseIds),
      };

      const result = await callApi(
        `sysadmin/adjustment`,
        HttpMethod.POST,
        adjustmentData,
      );

      handleCancel();
      setSuccessMessage(
        `Adjustment ${result.adjustmentNum} recorded successfully!`,
      );
    } catch (err: any) {
      let errorMessage = "Failed to record adjustment";

      if (err.data) {
        const errorData = err.data;
        errorMessage = errorData.message || errorMessage;
        if (errorData.error) {
          errorMessage += `\n\nError Details: ${errorData.error}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getDaysInMonth = (monthName: string, year: string): number => {
    const monthIndex = months.indexOf(monthName);
    if (monthIndex === -1) return 31;
    // Day 0 of the next month gives the last day of the current month
    return new Date(parseInt(year), monthIndex + 1, 0).getDate();
  };

  const generateDayOptions = () => {
    const daysInMonth = getDaysInMonth(formData.receivedMonth, formData.receivedYear);
    return Array.from({ length: daysInMonth }, (_, i) =>
      (i + 1).toString().padStart(2, "0"),
    );
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) =>
      (currentYear - 5 + i).toString(),
    );
  };

  const generateHourOptions = () => {
    return Array.from({ length: 12 }, (_, i) =>
      (i + 1).toString().padStart(2, "0"),
    );
  };

  const generateMinuteOptions = () => {
    return Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, "0"));
  };


  return (
    <div className={styles.container}>
      {error && (
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <pre
            className="mb-0"
            style={{ whiteSpace: "pre-wrap", fontSize: "0.9rem" }}
          >
            {error}
          </pre>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success" role="alert">
          {successMessage}
        </div>
      )}

      <div className="card" style={{ position: "relative" }}>
        {isLoading && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              borderRadius: "inherit",
            }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Recording adjustment...</span>
            </div>
          </div>
        )}
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row mb-1">
              <label
                htmlFor="billingNum"
                className={`col-sm-2 col-form-label ${styles.formLabel}`}
              >
                Billing Num:
              </label>
              <div className="col-sm-4">
                <input
                  type="text"
                  className="form-control"
                  id="billingNum"
                  name="billingNum"
                  value={formData.billingNum}
                  readOnly
                />
              </div>
            </div>

            <div className="row mb-1">
              <label
                htmlFor="amount"
                className={`col-sm-2 col-form-label ${styles.formLabel}`}
              >
                <span className="text-danger">*</span> Amount:
              </label>
              <div className="col-sm-4">
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="row mb-1">
              <label
                htmlFor="invoiceId"
                className={`col-sm-2 col-form-label ${styles.formLabel}`}
              >
                <span className="text-danger">*</span> For Invoice:
              </label>
              <div className="col-sm-6">
                <select
                  className="form-select"
                  id="invoiceId"
                  name="invoiceId"
                  value={formData.invoiceId}
                  onChange={handleInputChange}
                >
                  <option value="">-- Select an invoice --</option>
                  {invoiceOptions.map((invoice) => (
                    <option key={invoice.id} value={invoice.id}>
                      {invoice.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {formData.invoiceId && (
              <div className="row mb-1">
                <label className={`col-sm-2 col-form-label ${styles.formLabel}`}>
                  <span className="text-danger">*</span> Purchases:
                </label>
                <div className="col-sm-10">
                  {purchasesLoading ? (
                    <div className="d-flex align-items-center py-2">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading purchases...
                    </div>
                  ) : purchases.length > 0 ? (
                    <ul className="list-unstyled border rounded p-2 mb-0">
                      {purchases.map((purchase) => {
                        const priceParts: string[] = [];
                        if (purchase.onetime !== 0)
                          priceParts.push(`$${purchase.onetime.toFixed(2)} one-time`);
                        if (purchase.prorated !== 0)
                          priceParts.push(`$${purchase.prorated.toFixed(2)} prorated`);
                        if (purchase.monthly !== 0)
                          priceParts.push(`$${purchase.monthly.toFixed(2)}/mo`);
                        const priceDisplay = priceParts.length > 0
                          ? priceParts.join(", ")
                          : "$0.00";

                        return (
                          <li key={purchase.id} className="d-flex align-items-center gap-2">
                            <div className="form-check mb-0">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`purchase-${purchase.id}`}
                                checked={selectedPurchaseIds.has(purchase.id)}
                                onChange={() => handlePurchaseToggle(purchase.id)}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`purchase-${purchase.id}`}
                              >
                                {purchase.purchaseText || `SKU ${purchase.skuId}`}
                                {purchase.qty > 1 && ` (x${purchase.qty})`}
                                {" — "}
                                <span className="text-muted">{priceDisplay}</span>
                              </label>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <span className="text-muted py-2 d-inline-block">
                      No purchases found for this invoice.
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="row mb-1">
              <label className={`col-sm-2 col-form-label ${styles.formLabel}`}>
                Received On:
              </label>
              <div className="col-sm-10">
                <div className="row g-2">
                  <div className="col-auto">
                    <select
                      className="form-select"
                      name="receivedMonth"
                      value={formData.receivedMonth}
                      onChange={handleInputChange}
                    >
                      {months.map((month) => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-auto">
                    <select
                      className="form-select"
                      name="receivedDay"
                      value={formData.receivedDay}
                      onChange={handleInputChange}
                    >
                      {generateDayOptions().map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-auto">
                    <select
                      className="form-select"
                      name="receivedYear"
                      value={formData.receivedYear}
                      onChange={handleInputChange}
                    >
                      {generateYearOptions().map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-auto">
                    <select
                      className="form-select"
                      name="receivedHour"
                      value={formData.receivedHour}
                      onChange={handleInputChange}
                    >
                      {generateHourOptions().map((hour) => (
                        <option key={hour} value={hour}>
                          {hour}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-auto">
                    <span className="align-self-center">:</span>
                  </div>
                  <div className="col-auto">
                    <select
                      className="form-select"
                      name="receivedMinute"
                      value={formData.receivedMinute}
                      onChange={handleInputChange}
                    >
                      {generateMinuteOptions().map((minute) => (
                        <option key={minute} value={minute}>
                          {minute}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-auto">
                    <select
                      className="form-select"
                      name="receivedAmPm"
                      value={formData.receivedAmPm}
                      onChange={handleInputChange}
                    >
                      <option value="am">am</option>
                      <option value="pm">pm</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb-1">
              <label
                htmlFor="notes"
                className={`col-sm-2 col-form-label ${styles.formLabel}`}
              >
                Notes:
              </label>
              <div className="col-sm-10">
                <textarea
                  className="form-control"
                  id="notes"
                  name="notes"
                  rows={4}
                  value={formData.notes}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="row mb-1">
              <div className="col-sm-10 offset-sm-2">
                (Adjustment information cannot be edited via this interface
                after submission)
              </div>
            </div>

            <div className="row mb-1">
              <div className="col-sm-10 offset-sm-2">
                <button
                  type="submit"
                  className="btn btn-success me-3"
                  disabled={isLoading || !formData.amount || !formData.invoiceId || selectedPurchaseIds.size === 0}
                >
                  {isLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Recording Adjustment...
                    </>
                  ) : (
                    "Enter Adjustment"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>

          <hr className="my-4" />

          <div className="row">
            <div className="col-12">
              <p>
                <strong>*</strong> Specify amount of adjustment as a positive or
                negative number, cents optional, no dollar sign necessary. Enter
                a negative number to credit the account and forgive a balance
                due. Enter a positive number to offset a balance that we owe
                them.
              </p>
              <p>
                Select an invoice and check the purchase(s) this adjustment
                applies to.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordAdjustment;
